import { Component, OnInit, OnDestroy, ElementRef, signal, HostListener, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil, catchError, of } from 'rxjs';
import { CustomerProfileService } from '../../_services/api/customer-profile.service';
import { CustomerProfileResponse, CustomerProfileUpdate, PasswordUpdate } from '../../_models/profile/customer-profile.model';
import { ApiResponse } from '../../_models/api-response.model';
import { NotificationService } from '../../_services/app/notification.service';
import { Address } from '../../_models/address.model';
import { MenuStateService } from '../../_services/app/menu-state.service';
import { UserProfileService } from '../../_services/app/profile.service';

@Component({
  selector: 'app-member-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './member-profile.html',
  styleUrl: './member-profile.scss',
})
export class MemberProfile implements OnInit, OnDestroy {
  customerProfile: CustomerProfileResponse | null = null;
  profileForm: FormGroup;
  addressForm: FormGroup;
  isLoading = false;
  isEditMode = false;
  isSubmitting = false;
  showSuccess = false;
  successMessage = '';
  errorMessage = '';
  
  // file upload
  selectedFile: File | null = null;
  previewUrl: string | null = null;

  // password
  showPasswordForm = false;
  passwordForm!: FormGroup;
  isChangingPassword = false;
  passwordError = '';
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  
  private destroy$ = new Subject<void>();
  menuOpen = signal(false);

  constructor(
    private fb: FormBuilder,
    private customerProfileService: CustomerProfileService, 
    private profileService: UserProfileService,
    private notification: NotificationService, 
    private menuStateService: MenuStateService,
    private elementRef: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.addressForm = this.fb.group({
      streetNumber: ['', [Validators.required, Validators.maxLength(200)]],
      street: ['', Validators.maxLength(200)],
      city: ['', [Validators.required, Validators.maxLength(100)]],
      postalCode: ['', [Validators.required, Validators.pattern(/^[0-9]{4,10}$/)]],
    });

    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^[\d\s\+\-\(\)]{8,20}$/)]],
      companyName: ['', Validators.maxLength(200)],
      vatNumber: ['', Validators.maxLength(50)],
      address: this.addressForm
    });

    this.passwordForm = this.fb.group({
        currentPassword: ['', [Validators.required]],
        newPassword: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]]
    }, { validator: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.loadCustomerProfile();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();    
    if (isPlatformBrowser(this.platformId)) {
        document.removeEventListener('click', this.closeMenu.bind(this));
    }
  }

  private loadCustomerProfile() {
    this.customerProfileService.getCustomerProfile().pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading profile:', error);
        this.notification.showError('Erreur lors du chargement du profil');
        this.isLoading = false;
        return of(null);
      })
    ).subscribe(response => {
      if (response && response.success && response.data) {
        this.customerProfile = response.data;
        this.profileService.notifyOtherTabs();
        this.previewUrl = this.customerProfile.profileImage || null;
        this.populateForm();

        this.menuStateService.activeMenu$.pipe(
          takeUntil(this.destroy$)
        ).subscribe(activeId => {
          this.menuOpen.set(activeId === this.customerProfile!.id);
        });

        if (isPlatformBrowser(this.platformId)) {
            document.addEventListener('click', this.closeMenu.bind(this));
        }
        this.isLoading = false;
        if (isPlatformBrowser(this.platformId)) {
          window.scrollTo(0, 0);
        }
      } else {
        return;
      }      
    });
  }

  private populateForm(): void {
    if (!this.customerProfile) return;

    if (this.customerProfile.address) {
      this.addressForm.patchValue(this.customerProfile.address);
    }

    this.profileForm.patchValue({
      firstName: this.customerProfile.firstName,
      lastName: this.customerProfile.lastName,
      phoneNumber: this.customerProfile.phoneNumber,
      companyName: this.customerProfile.companyName || '',
      vatNumber: this.customerProfile.vatNumber || ''
    });
  }

  private passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
        return { 'passwordMismatch': true };
    }
    return null;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      if (!file.type.startsWith('image/')) {
        this.notification.showError('Veuillez sélectionner une image valide (JPG, PNG, GIF)');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        this.notification.showError('L\'image ne doit pas dépasser 5MB');
        return;
      }
      
      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    if (this.customerProfile?.profileImage) {
      this.previewUrl = this.customerProfile.profileImage;
    } else {
      this.previewUrl = null;
    }
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    if (this.isEditMode && this.customerProfile) {
      this.populateForm();
    }
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.showPasswordForm = false;
    this.selectedFile = null;
    if (this.customerProfile?.profileImage) {
      this.previewUrl = this.customerProfile.profileImage;
    } else {
      this.previewUrl = null;
    }
  }

  onSubmit(): void {
    if (this.profileForm.invalid || this.addressForm.invalid) {
      this.markFormGroupTouched(this.profileForm);
      this.markFormGroupTouched(this.addressForm);
      this.notification.showError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    this.isSubmitting = true;
    const profileUpdate: CustomerProfileUpdate = this.buildProfileUpdate();
    
    this.customerProfileService.updateCustomerProfile(profileUpdate, this.selectedFile || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<CustomerProfileResponse>) => {
          this.customerProfile = response.data;
          this.isEditMode = false;
          this.selectedFile = null;
          this.previewUrl = this.customerProfile.profileImage || null;
          this.showSuccess = true;
          this.successMessage = 'Profil mis à jour avec succès';
          this.notification.showSuccess('Profil mis à jour avec succès');
          this.isSubmitting = false;
          if (isPlatformBrowser(this.platformId)) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        },
        error: (error) => {
          console.error('Erreur lors de la mise à jour du profil:', error);
          this.notification.showError('Erreur lors de la mise à jour du profil');
          this.isSubmitting = false;
        }
      });
  }

  private buildProfileUpdate(): CustomerProfileUpdate {
    const formValue = this.profileForm.value;
    const address: Address = {
      streetNumber: formValue.address.streetNumber,
      street: formValue.address.street,
      city: formValue.address.city,
      postalCode: formValue.address.postalCode
    };

    return {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      phoneNumber: formValue.phoneNumber,
      companyName: formValue.companyName || undefined,
      vatNumber: formValue.vatNumber || undefined,
      address: address
    };
  }

  updateProfileImage(): void {
    if (!this.selectedFile) {
      this.notification.showError('Veuillez sélectionner une image');
      return;
    }

    this.isSubmitting = true;
    this.customerProfileService.updateCustomerProfileImage(this.selectedFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<CustomerProfileResponse>) => {
          this.customerProfile = response.data;
          this.selectedFile = null;
          this.previewUrl = this.customerProfile.profileImage || null;
          this.notification.showSuccess('Photo de profil mise à jour avec succès');
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Erreur lors de la mise à jour de la photo:', error);
          this.notification.showError('Erreur lors de la mise à jour de la photo');
          this.isSubmitting = false;
        }
      });
  }

  deleteProfileImage(): void {
    if (!confirm('Êtes-vous sûr de vouloir supprimer votre photo de profil ?')) {
      return;
    }

    this.isSubmitting = true;
    this.customerProfileService.deleteCustomerProfileImage()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (this.customerProfile) {
            this.customerProfile.profileImage = undefined;
          }
          this.previewUrl = null;
          this.selectedFile = null;
          this.notification.showSuccess('Photo de profil supprimée avec succès');
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Erreur lors de la suppression de la photo:', error);
          this.notification.showError('Erreur lors de la suppression de la photo');
          this.isSubmitting = false;
        }
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  togglePasswordForm(): void {
    this.showPasswordForm = !this.showPasswordForm;
    this.passwordForm.reset();
    this.passwordError = '';
  }

  togglePassword(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  onPasswordSubmit(): void {
    if (this.passwordForm.invalid) {
        this.markFormGroupTouched(this.passwordForm);
        return;
    }
    
    this.isChangingPassword = true;
    this.passwordError = '';
    
    const passwordUpdate: PasswordUpdate = {
        currentPassword: this.passwordForm.value.currentPassword,
        newPassword: this.passwordForm.value.newPassword,
        confirmPassword: this.passwordForm.value.confirmPassword
    };
    
    this.customerProfileService.updateCustomerPassword(passwordUpdate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
          next: () => {
              this.notification.showSuccess('Mot de passe mis à jour avec succès');
              this.passwordForm.reset();
              this.showPasswordForm = false;
              this.isChangingPassword = false;
          },
          error: (error) => {
              console.error('Erreur lors de la mise à jour du mot de passe:', error);
              this.passwordError = error.error?.message || 'Erreur lors de la mise à jour du mot de passe';
              this.notification.showError(this.passwordError);
              this.isChangingPassword = false;
          }
      });
  }

  toggleMenu(event: MouseEvent) {
    if(!this.customerProfile){return}
    event.stopPropagation();
    if (this.menuOpen()) {
      this.menuStateService.setActiveMenu(null);
    } else {
      this.menuStateService.setActiveMenu(this.customerProfile.id);
    }
  }

  @HostListener('document:click', ['$event'])
  closeMenu(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.menuOpen.set(false);
    }
    if (this.menuOpen()) {
      this.menuStateService.setActiveMenu(null);
    }
  }

  formatAddress(address: any): string {
    if (!address) return 'Non spécifiée';
    
    const parts: string[] = [];
    
    if (address.streetNumber && address.street) {
      parts.push(`${address.street} ${address.streetNumber}`);
    } else if (address.street) {
      parts.push(address.street);
    }
    
    if (address.postalCode && address.city) {
      parts.push(`${address.postalCode} ${address.city}`);
    } else if (address.postalCode) {
      parts.push(address.postalCode);
    } else if (address.city) {
      parts.push(address.city);
    }
    
    if (parts.length === 0) {
      return 'Non spécifiée';
    }
    
    return parts.join(', ') + ".";
  }

  get firstName() { return this.profileForm.get('firstName'); }
  get lastName() { return this.profileForm.get('lastName'); }
  get phoneNumber() { return this.profileForm.get('phoneNumber'); }
  get companyName() { return this.profileForm.get('companyName'); }
  get vatNumber() { return this.profileForm.get('vatNumber'); }

  get streetNumber() { return this.addressForm.get('streetNumber'); }
  get street() { return this.addressForm.get('street'); }
  get city() { return this.addressForm.get('city'); }
  get postalCode() { return this.addressForm.get('postalCode'); }

  get currentPassword() { return this.passwordForm.get('currentPassword'); }
  get newPassword() { return this.passwordForm.get('newPassword'); }
  get confirmPassword() { return this.passwordForm.get('confirmPassword'); }
}